<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Recursive node for the flow map. `node` is a pre-built plain tree:
//   { kind:'screen', id, isRoot, title, count, children:[] }
//   { kind:'shared', id, title }   (already shown elsewhere)
//   { kind:'leaf', label }         (button that hands to an agent)
const props = defineProps({
  node: { type: Object, required: true },
  currentId: { type: String, default: '' },
});
defineEmits(['select']);
const { t } = useI18n();

const isScreen = computed(() => props.node.kind === 'screen');
const isCurrent = computed(
  () => isScreen.value && props.node.id === props.currentId
);
const hasChildren = computed(
  () => isScreen.value && (props.node.children || []).length > 0
);
const meta = computed(() => {
  if (props.node.isRoot) return t('AUTO_REPLY_BOT_SETTINGS.MAP.FIRST_MSG');
  return props.node.count
    ? t('AUTO_REPLY_BOT_SETTINGS.MAP.BUTTONS_META', { count: props.node.count })
    : t('AUTO_REPLY_BOT_SETTINGS.MAP.END_META');
});
</script>

<template>
  <div>
    <!-- screen card -->
    <button
      v-if="isScreen"
      type="button"
      class="group inline-flex items-center gap-2.5 rounded-2xl border px-3 py-2 text-right shadow-sm transition-all hover:-translate-y-px hover:border-n-brand"
      :class="
        isCurrent
          ? 'border-n-brand bg-n-brand/10 ring-2 ring-n-brand/30'
          : node.isRoot
            ? 'border-transparent bg-n-brand/5'
            : 'border-n-weak bg-n-solid-1'
      "
      @click="$emit('select', node.id)"
    >
      <span
        class="grid size-9 shrink-0 place-items-center rounded-xl"
        :class="
          isCurrent ? 'bg-n-solid-1 text-n-brand' : 'bg-n-brand/10 text-n-brand'
        "
      >
        <span
          :class="node.isRoot ? 'i-lucide-hand' : 'i-lucide-message-square'"
          class="size-[18px]"
        />
      </span>
      <span class="flex flex-col">
        <span
          class="text-[13px] font-extrabold leading-tight"
          :class="isCurrent ? 'text-n-brand' : 'text-n-slate-12'"
        >
          {{ node.title }}
        </span>
        <span class="mt-0.5 text-[10.5px] font-semibold text-n-slate-10">
          {{ meta }}
        </span>
      </span>
      <span
        v-if="node.count"
        class="grid size-5 shrink-0 place-items-center rounded-md bg-n-brand/10 text-[10px] font-extrabold text-n-brand"
        dir="ltr"
      >
        {{ node.count }}
      </span>
    </button>

    <!-- shared reference -->
    <button
      v-else-if="node.kind === 'shared'"
      type="button"
      class="inline-flex items-center gap-2.5 rounded-2xl border border-dashed border-n-weak bg-n-alpha-black1 px-3 py-2 text-right opacity-90 transition-colors hover:border-n-brand"
      @click="$emit('select', node.id)"
    >
      <span
        class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-alpha-2 text-n-slate-10"
      >
        <span class="i-lucide-message-square size-[15px]" />
      </span>
      <span class="flex flex-col">
        <span class="text-[13px] font-extrabold leading-tight text-n-slate-12">
          {{ node.title }}
        </span>
        <span class="mt-0.5 text-[10.5px] font-semibold text-n-slate-10">
          {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.SHARED_META') }}
        </span>
      </span>
      <span
        class="whitespace-nowrap rounded-md bg-n-alpha-2 px-2 py-0.5 text-[10px] font-extrabold text-n-slate-10"
      >
        {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.SHARED') }}
      </span>
    </button>

    <!-- leaf (hand to agent) -->
    <div
      v-else
      class="inline-flex items-center gap-2.5 rounded-2xl border border-dashed border-n-weak px-3 py-2 text-right"
    >
      <span
        class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-alpha-2 text-n-slate-10"
      >
        <span class="i-lucide-corner-down-left size-[15px]" />
      </span>
      <span class="flex flex-col">
        <span class="text-[13px] font-bold leading-tight text-n-slate-11">
          {{ node.label }}
        </span>
        <span class="mt-0.5 text-[10.5px] font-semibold text-n-slate-10">
          {{ t('AUTO_REPLY_BOT_SETTINGS.MAP.LEAF_META') }}
        </span>
      </span>
    </div>

    <!-- children with tree connectors -->
    <ul v-if="hasChildren" class="m-0 list-none p-0">
      <li
        v-for="(child, i) in node.children"
        :key="i"
        class="relative flex items-stretch"
      >
        <span class="relative w-6 shrink-0">
          <span class="absolute end-3 top-0 h-1/2 w-[2px] bg-n-weak" />
          <span
            v-if="i < node.children.length - 1"
            class="absolute bottom-0 end-3 top-1/2 w-[2px] bg-n-weak"
          />
          <span
            class="absolute end-3 top-1/2 h-[2px] w-3 -translate-y-1/2 bg-n-weak"
          />
        </span>
        <div class="min-w-0 py-1.5">
          <FlowMapNode
            :node="child"
            :current-id="currentId"
            @select="$emit('select', $event)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
